import { cva } from 'class-variance-authority';
import { Badge } from '@nula/core-ui';
import { useAssistantNotificationsStore } from '../stores/use-assistant-notifications-store';
import styles from './conversation-badge.module.css';

const root = cva(styles.root);

interface ConversationBadgeProps {
  /** The conversation this badge annotates (tab / sidebar entry). */
  conversationId: string;
}

/**
 * Quiet background-conversation badge (design §8.4).
 *
 * - needs-approval  → distinct "needs you" warning dot (takes precedence)
 * - done            → quiet success completion dot
 * - neither         → renders nothing
 *
 * Color is token-backed: core-ui Badge's `status` dot mode maps
 * 'warning' → var(--warning) and 'success' → var(--success) internally. We
 * never pass a literal color (CLAUDE.md design-token rule).
 *
 * The css-modules root class is applied via Badge's className prop (css-modules
 * convention: every component has a cva root, even when zero-variant and empty).
 */
export function ConversationBadge({ conversationId }: ConversationBadgeProps) {
  const note = useAssistantNotificationsStore((s) => s.byConversation[conversationId]);
  if (!note) return null;

  if (note.needsApproval) {
    return (
      <Badge
        className={root()}
        status="warning"
        aria-label="Needs your approval"
        data-testid="conversation-badge"
        data-state="needs-approval"
      />
    );
  }
  if (note.done) {
    return (
      <Badge
        className={root()}
        status="success"
        aria-label="Run complete"
        data-testid="conversation-badge"
        data-state="done"
      />
    );
  }
  return null;
}
